<template>
  <div class="flex flex-col space-y-8 py-2.5 px-3">
    <div id="written-student-reports" class="px-5 flex flex-col space-y-3">
      <div class="flex items-center space-x-3 mb-3">
        <div class="h-8 w-8 bg-blue rounded-full flex items-center justify-center">
          <img src="/static/roundtable_icons/written_icon.svg" class="w-4 h-4"/>
        </div>
        <h4 class="text-blueGray-900 font-semibold text-lg font-poppins">Written RoundTable</h4>
      </div>
      <div id="skills-assessment-container" class="border border-solid border-blueGray-200 rounded flex flex-col space-y-3">
        <div class="px-5 py-3.5 flex flex-col">
          <h6 class="text-blueGray-800 font-poppins text-base font-semibold">Skills Assessment</h6>
          <span class="text-blueGray-500">
            Below is a view of the skills you demonstrated in each Written RoundTable, as assessed by your facilitator.
          </span>
        </div>
        <div id="skills-summary-table">
          <SkillsSummary/>
        </div>
      </div>
      <div id="participation-summary-container" class="border border-solid border-blueGray-200 rounded flex flex-col space-y-3">
        <div class="px-5 py-3.5 flex flex-col">
          <h6 class="text-blueGray-800 font-poppins font-semibold text-base">Participation Summary</h6>
          <span class="text-blueGray-500">
            Below is a view of your participation in each Written RoundTable including the number of original responses, comments and replies.
          </span>
        </div>
        <div id="participation-summary-chart">
          <ParticipationChart/>
        </div>
      </div>
    </div>
    <div id="verbal-student-reports" class="px-5 flex flex-col space-y-3">
      <div class="flex items-center space-x-3 mb-3">
        <div class="h-8 w-8 bg-green-dark rounded-full flex items-center justify-center">
          <img src="/static/roundtable_icons/verbal_icon.svg" class="w-4 h-4"/>
        </div>
        <h4 class="text-blueGray-900 font-semibold text-lg font-poppins">Verbal RoundTable</h4>
      </div>
      <div id="tapin-container" class="border border-solid border-blueGray-200 rounded flex flex-col space-y-3">
        <div class="px-5 py-3.5 flex flex-col">
          <h6 class="text-blueGray-800 font-poppins font-semibold text-base">Tap In Summary</h6>
          <span class="text-blueGray-500">
            Below is a view of the discussion skills you demonstrated during your speaking turns in each Verbal RoundTable.
          </span>
        </div>
        <div id="taps-summary-chart">
          <TapsSummary/>
        </div>
      </div>
      <div id="speaking-summary-container" class="border border-solid border-blueGray-200 rounded flex flex-col space-y-3">
        <div class="px-5 py-3.5 flex flex-col space-y-1">
          <h6 class="text-blueGray-800 font-poppins font-semibold text-base">Speaking Summary</h6>
          <span class="text-blueGray-600">
            Below is a view of your time as the speaker in each Verbal RoundTable, compared to the class average.
          </span>
        </div>
        <div id="speaking-summary-chart">
          <SpeakingChart/>
        </div>
      </div>
    </div>
    <div id="student-refections" class="px-5 flex flex-col space-y-3">
      <div class="flex flex-col space-y-2">
        <div class="flex items-center space-x-2.5">
          <div class="h-8 w-8 bg-orange rounded-full flex items-center justify-center">
            <img src="/static/roundtable_icons/reflections.svg" class="w-4 h-4"/>
          </div>
          <h4 class="text-blueGray-900 font-semibold text-lg font-poppins">Reflection</h4>
        </div>
        <div class="text-blueGray-600">
          <span>
            Use the space below to reflect on your progress and contributions so far. Where are you succeeding? What have you learned? What skill would you like to improve on next?
          </span>
        </div>
      </div>
      <div id="reflections-container" class="border border-solid border-blueGray-200 rounded flex flex-col space-y-3">
        <textarea 
          :disabled="hasTeacherPermissionsInCourse || getProfile.role === 'teacher'" 
          @input="onInput" 
          v-model="reflection.text" 
          :placeholder="hasTeacherPermissionsInCourse ? 'No reflections from this student...': 'Write your reflections on your progress and contributions.'" 
          rows="11" 
          :class="hasTeacherPermissionsInCourse || getProfile.role === 'teacher' ? 'resize-none' : ''"
          class="outline-none bg-white py-2.5 px-3.5 rounded"
        >

        </textarea>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue';
  import axios from 'axios';
  import { mapGetters } from 'vuex';
  import ParticipationChart from './WrittenCharts/ParticipationChart.vue';
  import SpeakingChart from './VerbalCharts/SpeakingChart.vue';
  import SkillsSummary from './WrittenCharts/SkillsSummary.vue';
  import TapsSummary from './VerbalCharts/TapsSummary.vue';
  export default Vue.extend({
    data() {
      return {
        reflection: {
          text: null,
          id: null,
        },
        saveTimer: null,
        message: '',
        isLoading: false,
      };
    },
    watch: {
      async '$route.params.studentID'() {
        this.reflection.text = '';
        this.reflection.id = '';
        this.handleGetReflection();
      }
    },
    components: {
      SkillsChart: () => import('./SectionsSummary/SkillsRoundTable.vue'),
      OnlineStatisticsStudent: () => import('./SectionsSummary/OnlineStatisticsStudent.vue'),
      OnlineStatisticsStudentContributions: () => import('./SectionsSummary/OnlineContributionsStudent.vue'),
      LiveStatisticsTapsStudent: () => import('./SectionsSummary/LiveRoundTableTapsStudent.vue'),
      LiveTimeSpeakerStudent: () => import('./SectionsSummary/LiveTimeStudentSpeaker.vue'),
      ParticipationChart,
      SpeakingChart,
      SkillsSummary,
      TapsSummary
    },
    computed: {
      ...mapGetters([
        'hasTeacherPermissionsInCourse',
        'getProfile'
      ]),
    },
    methods: {
      onInput() {
        if (this.saveTimer) {
          clearTimeout(this.saveTimer);
        }
        this.saveTimer = setTimeout(this.autoSave, 1000);
      },
      autoSave: async function() {
        this.message = `Saving...`;
        await axios.put(`/api/reflections/update-reflection/${this.reflection.id}`, {
          reflection: this.reflection.text,
        });
        setTimeout(() => {
          this.message = 'Saved';
        }, 2500);
      },
      handleGetReflection: async function() {
        this.isLoading = true;
        const course_id = this.$route.params.courseID;
        const student_id = this.$route.params.studentID;
        if (this.getProfile.role === 'teacher' || this.hasTeacherPermissionsInCourse) {
          const { data } = await axios.get(`/api/reflections/student/${student_id}/course/${course_id}`);
          this.reflection.id  = data.id;
          this.reflection.text = data.reflection;
        } else {
          const { data } = await axios.get(`/api/reflections/my-reflection/${course_id}`);
          this.reflection.id  = data.id;
          this.reflection.text = data.reflection;
        }
        this.isLoading = false;
      }
    },
    mounted: async function() {
      if (this.getProfile.is_paid === true) {
        this.handleGetReflection();
      }
      
    },
    beforeRouteLeave: async function() {
      const chartParticipation = document.getElementById('participationChartReports');
      if (chartParticipation) {
        chartParticipation.remove();
      }
    }
  });
</script>



// WEBPACK FOOTER //
// SummaryTab.vue?12785a74